import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {useAuth} from "../../modules/auth";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import axios from "axios";
import {ENTITY_ID_ATTENDANCE, ENTITY_ID_WORKER} from "../generic/Constants";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {useFormik} from "formik";
import {apiGenericCreate} from "../generic/crud";

export function AttendanceWorkerPage() {
    const myData = useContext(TrackVASContext);
    const {currentUser} = useAuth();
    const {branchId} = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [attendanceList, setAttendanceList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(
        location.state?.selectedDate || new Date().toISOString().split('T')[0]
    );

    const branchName = location.state?.branchName || "";
    const dealerName = location.state?.dealerName || "";

    const formik = useFormik({
        initialValues: {
            selectedWorker: null
        },
        onSubmit: (values) => {
            handleAddWorker(values.selectedWorker);
        }
    });

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
        {
            title: "Attendance",
            path: "/attendance/branches",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
        });

        fetchAttendance();

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, [branchId, selectedDate]);

    const fetchAttendance = () => {
        setIsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;

        // Convert selectedDate to Date object for filtering
        const dateObj = new Date(selectedDate);
        const startOfDay = new Date(dateObj.setHours(0, 0, 0, 0)).getTime();
        const endOfDay = new Date(dateObj.setHours(23, 59, 59, 999)).getTime();

        // Fetch attendance records for this branch and date
        axios.get(`${apiUrl}/api/generic/all`, {
            params: {
                entityId: ENTITY_ID_ATTENDANCE,
                page: 1,
                sizePerPage: 500,
                currentIndex: 0,
                sortBy: 'workerName',
                fixedFilterData: branchId,
                fixedFilterColumn: 'branchId'
            }
        }).then(({data}) => {
            // Filter by date on client side (since backend filter might not support date range)
            const filteredData = (data.entities || []).filter(record => {
                const recordDate = new Date(record.attendanceDate).getTime();
                return recordDate >= startOfDay && recordDate <= endOfDay;
            });
            setAttendanceList(filteredData);
            setIsLoading(false);
        }).catch(() => {
            setAttendanceList([]);
            setIsLoading(false);
        });
    };

    const [pendingWorkers, setPendingWorkers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ... existing useEffect ...

    const handleAddWorker = (worker) => {
        if (!worker || !worker.id) {
            showToast("Please select a worker", TOAST_FAILURE);
            return;
        }

        // Check if worker already marked (saved)
        const alreadyMarked = attendanceList.find(a => a.workerId === worker.id);
        if (alreadyMarked) {
            showToast("Worker already marked for attendance today", TOAST_FAILURE);
            return;
        }

        // Check if worker already in pending list
        const alreadyPending = pendingWorkers.find(w => w.id === worker.id);
        if (alreadyPending) {
            showToast("Worker is already in the pending list", TOAST_FAILURE);
            return;
        }

        setPendingWorkers([...pendingWorkers, worker]);
        formik.setFieldValue('selectedWorker', null);
    };

    const handleRemovePendingWorker = (workerId) => {
        setPendingWorkers(pendingWorkers.filter(w => w.id !== workerId));
    };

    const handleSubmitAttendance = async () => {
        if (pendingWorkers.length === 0) return;

        setIsSubmitting(true);
        const promises = pendingWorkers.map(worker => {
            const attendanceData = {
                branchId: branchId,
                workerId: worker.id,
                attendanceDate: new Date(selectedDate).toISOString(),
                supervisorId: currentUser.userId
            };
            return apiGenericCreate(ENTITY_ID_ATTENDANCE, attendanceData)
                .then(() => ({ success: true, workerName: worker.name }))
                .catch((error) => ({
                    success: false,
                    workerName: worker.name,
                    error: error.response?.data || error.message || "Unknown error"
                }));
        });

        const results = await Promise.allSettled(promises);

        const successfulWorkers = [];
        const failedWorkers = [];

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                const data = result.value;
                if (data.success) {
                    successfulWorkers.push(data.workerName);
                } else {
                    failedWorkers.push({ name: data.workerName, error: data.error });
                }
            } else {
                failedWorkers.push({
                    name: pendingWorkers[index].name,
                    error: result.reason?.message || "Unknown error"
                });
            }
        });

        // Show success message
        if (successfulWorkers.length > 0) {
            showToast(`Successfully marked attendance for ${successfulWorkers.length} worker(s)`, TOAST_SUCCESS);
        }

        // Show failure messages
        if (failedWorkers.length > 0) {
            const failureMessages = failedWorkers.map(w => `${w.name}: ${w.error}`).join('\n');
            showToast(
                `Failed to mark ${failedWorkers.length} worker(s):\n${failureMessages}`,
                TOAST_FAILURE
            );
        }

        setPendingWorkers([]);
        fetchAttendance();
        setIsSubmitting(false);
    };

    const handleRemoveWorker = async (attendanceId) => {
        const result = await showConfirmDialog("Remove this worker from today's attendance?");
        if (result) {
            const apiUrl = process.env.REACT_APP_API_URL;
            try {
                await axios.delete(`${apiUrl}/api/generic/delete/${attendanceId}`, {
                    params: {
                        entityId: ENTITY_ID_ATTENDANCE
                    }
                });
                showToast("Worker removed from attendance", TOAST_SUCCESS);
                fetchAttendance();
            } catch (error) {
                showToast("Failed to remove worker", TOAST_FAILURE);
            }
        }
    };

    const AttendanceCard = ({record, index, onRemove, isPending = false}) => (
        <div className={`card border shadow-sm mb-4 ${isPending ? 'border-warning bg-light-warning' : 'border-secondary'}`}>
            <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div className="symbol symbol-45px me-3 symbol-circle">
                            <span className={`symbol-label ${isPending ? 'bg-warning text-white' : 'bg-light-success text-success'} fw-bolder fs-3`}>
                                {record.workerName ? record.workerName.charAt(0).toUpperCase() : (record.name ? record.name.charAt(0).toUpperCase() : '?')}
                            </span>
                        </div>
                        <div className="d-flex flex-column">
                            <span className="text-gray-900 fw-bolder fs-6">{record.workerName || record.name}</span>
                            {isPending ? (
                                <span className="badge badge-warning w-auto mt-1">Pending Save</span>
                            ) : (
                                <span className="text-gray-500 fs-8">Marked: {record.createdOn ? new Date(record.createdOn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}</span>
                            )}
                        </div>
                    </div>
                    <button
                        className="btn btn-icon btn-light-danger btn-sm"
                        onClick={() => onRemove(isPending ? record.id : record.id)}
                        title="Remove"
                    >
                        <i className="bi bi-trash fs-4"></i>
                    </button>
                </div>
                {!isPending && (
                    <>
                        <div className="separator separator-dashed my-3"></div>
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="text-gray-600 fs-8 fw-bold">Marked By:</span>
                            <span className="text-gray-800 fs-8 fw-semibold">{record.supervisorName || 'N/A'}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>
                Attendance - {branchName}
            </PageTitle>
            <div className="card card-body p-4 p-md-6">
                {/* Branch and Date Header - Mobile Optimized */}
                <div className="mb-6">
                    <div className="card bg-light-primary border-0">
                        <div className="card-body p-4">
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <h4 className="fw-bolder text-gray-900 mb-1 fs-5">{branchName}</h4>
                                    {dealerName && (
                                        <div className="d-flex align-items-center text-gray-600 fs-7">
                                            <i className="bi bi-shop me-2 text-gray-500"></i>
                                            {dealerName}
                                        </div>
                                    )}
                                </div>
                                <div className="separator separator-dashed border-primary opacity-25"></div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center text-primary fw-bold">
                                        <i className="bi bi-calendar-check fs-4 me-2"></i>
                                        <span>Date</span>
                                    </div>
                                    <input
                                        type="date"
                                        className="form-control form-control-solid form-control-sm w-auto"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        style={{ colorScheme: 'dark' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Worker Section - Stacked for Mobile */}
                <div className="mb-6">
                    <h5 className="fw-bolder text-gray-900 mb-4 fs-6">
                        Add Worker
                    </h5>
                    <div className="d-flex flex-column gap-3">
                        <div className="w-100">
                            <AGCSelect
                                entityId={ENTITY_ID_WORKER}
                                formik={formik}
                                isMulti={false}
                                name="Search Worker"
                                value={formik.values.selectedWorker}
                                id="selectedWorker"
                                required={false}
                                placeholder="Select a worker..."
                            />
                        </div>
                        <button
                            className="btn btn-primary w-100 py-3"
                            onClick={() => formik.handleSubmit()}
                            disabled={!formik.values.selectedWorker}
                        >
                            <i className="bi bi-plus-lg me-2"></i>
                            Add to List
                        </button>
                    </div>
                </div>

                <div className="separator separator-dashed my-6"></div>

                {/* Pending Workers Section */}
                {pendingWorkers.length > 0 && (
                    <div className="mb-8">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="fw-bolder text-gray-900 fs-6 m-0">
                                To Be Marked ({pendingWorkers.length})
                            </h5>
                        </div>
                        <div className="row g-3 mb-4">
                            {pendingWorkers.map((worker, index) => (
                                <div key={worker.id} className="col-12 col-md-6 col-xl-4">
                                    <AttendanceCard 
                                        record={worker} 
                                        index={index} 
                                        onRemove={handleRemovePendingWorker} 
                                        isPending={true}
                                    />
                                </div>
                            ))}
                        </div>
                        <button 
                            className="btn btn-success w-100 py-3 fw-bold"
                            onClick={handleSubmitAttendance}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check2-circle me-2 fs-2"></i>
                                    Submit Attendance
                                </>
                            )}
                        </button>
                        <div className="separator separator-dashed my-6"></div>
                    </div>
                )}

                {/* Attendance List - Card View */}
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="fw-bolder text-gray-900 fs-6 m-0">
                            Already Marked ({attendanceList.length})
                        </h5>
                    </div>

                    {isLoading && (
                        <div className="text-center py-10">
                            <span className="spinner-border spinner-border-lg text-primary"></span>
                            <p className="mt-3 text-gray-500 fs-7">Loading list...</p>
                        </div>
                    )}

                    {!isLoading && attendanceList.length === 0 && pendingWorkers.length === 0 && (
                        <div className="text-center py-10 bg-light rounded-3 border border-dashed border-secondary">
                            <i className="bi bi-people fs-1 text-gray-400 mb-3"></i>
                            <h6 className="text-gray-600 fw-bold mb-1">No workers marked</h6>
                            <span className="text-gray-500 fs-8">Use the search box above to add workers.</span>
                        </div>
                    )}

                    {!isLoading && attendanceList.length > 0 && (
                        <div className="row g-3">
                            {attendanceList.map((record, index) => (
                                <div key={record.id} className="col-12 col-md-6 col-xl-4">
                                    <AttendanceCard 
                                        record={record} 
                                        index={index} 
                                        onRemove={handleRemoveWorker} 
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Back Button */}
                <div className="mt-8">
                    <button
                        className="btn btn-secondary w-100 py-3 fw-bold"
                        onClick={() => navigate('/attendance/branches')}
                    >
                        <i className="bi bi-arrow-left me-2"></i>
                        Back to Branches
                    </button>
                </div>
            </div>
        </>
    );
}
